import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import api, { formatApiError } from "@/lib/api";
import { Check, X, Shield, FileCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminApplications() {
  const [applications, setApplications] = useState([]);
  const ctl = useListControls(applications, { name: (a) => a.name, fields: (a) => [a.name, a.email] });
  const [filter, setFilter] = useState("pending");
  const [activeReject, setActiveReject] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/coach-applications${filter ? `?filter_status=${filter}` : ""}`);
    setApplications(data);
  };

  useEffect(() => { load();   }, [filter]);

  const review = async (id, action, reasonText = "") => {
    try {
      await api.post(`/admin/coach-applications/${id}/review`, { action, reason: reasonText });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setActiveReject(null);
      setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2 flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-[#CCFF00]" /> ADMIN · APPLICATIONS</div>
          <h1 className="h-display text-5xl tracking-tighter">Coach onboarding queue.</h1>
        </div>
        <div className="inline-flex border border-[#262626]" data-testid="filter-tabs">
          {[
            { v: "pending", l: "Pending" },
            { v: "approved", l: "Approved" },
            { v: "rejected", l: "Rejected" },
            { v: "", l: "All" },
          ].map((t) => (
            <button key={t.v} onClick={() => setFilter(t.v)} data-testid={`filter-${t.v || "all"}`}
              className="px-4 py-2 text-xs uppercase tracking-wider transition-colors"
              style={filter === t.v ? { background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 } : { color: "#A3A3A3" }}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {!applications.length && (
        <div className="card-surface p-12 text-center fade-up d1">
          <FileCheck className="w-12 h-12 text-[#262626] mx-auto mb-4" strokeWidth={1.5} />
          <div className="h-display text-2xl">No applications in this status</div>
        </div>
      )}

      <div className="space-y-3 fade-up d1">
        {applications.length > 0 && <ListControlsBar ctl={ctl} placeholder="Search applicants…" testid="applications" />}
        {ctl.list.map((a) => {
          const app = a.coach_application || {};
          const review_data = a.coach_review;
          const statusColor = a.coach_status === "approved" ? "#CCFF00" : a.coach_status === "rejected" ? "#FF4500" : "#007AFF";
          return (
            <div key={a.id} className="card-surface p-6" data-testid={`application-${a.id}`}>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-mono" style={{ color: statusColor }}>{a.coach_status?.toUpperCase()}</span>
                    {app.years_experience > 0 && <span className="label-mono">· {app.years_experience}y experience</span>}
                  </div>
                  <h3 className="h-display text-2xl tracking-tight">{a.name}</h3>
                  <div className="text-sm text-[#A3A3A3]">{a.email}</div>
                </div>
                {a.coach_status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => review(a.id, "approve")} data-testid={`approve-${a.id}`}
                      className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button onClick={() => setActiveReject(a.id)} data-testid={`reject-${a.id}`}
                      className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#FF4500] hover:text-[#FF4500] inline-flex items-center gap-1.5">
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                )}
              </div>

              {app.certification ? (
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm border-t border-[#262626] pt-4">
                  <Row k="Certification" v={app.certification} />
                  <Row k="Awarding body" v={app.cert_body} />
                  <Row k="Insurance provider" v={app.insurance_provider} />
                  <Row k="Policy number" v={app.insurance_policy_number} />
                  <Row k="Policy expiry" v={app.insurance_expiry} />
                  <Row k="Submitted" v={app.submitted_at?.slice(0, 10)} />
                  {app.bio && <div className="md:col-span-2"><Row k="Bio" v={app.bio} /></div>}
                </div>
              ) : (
                <div className="text-sm text-[#A3A3A3]">Application not yet submitted.</div>
              )}

              {review_data && (
                <div className="mt-4 text-xs border-l-2 pl-3" style={{ borderColor: statusColor }}>
                  <span className="label-mono" style={{ color: statusColor }}>{review_data.action?.toUpperCase()}</span>
                  {review_data.reason && <span className="text-[#A3A3A3] ml-2">· {review_data.reason}</span>}
                </div>
              )}

              {activeReject === a.id && (
                <div className="mt-4 p-3 border border-[#FF4500] bg-[#FF4500]/5" data-testid={`reject-form-${a.id}`}>
                  <div className="label-mono mb-2 text-[#FF4500]">Reason (visible to coach)</div>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                    placeholder="e.g. Insurance certificate not provided. Please re-upload."
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#FF4500] mb-2" />
                  <div className="flex gap-2">
                    <button onClick={() => review(a.id, "reject", reason)} className="px-4 py-2 text-xs uppercase tracking-wider bg-[#FF4500] text-white font-bold" data-testid={`confirm-reject-${a.id}`}>
                      Confirm reject
                    </button>
                    <button onClick={() => { setActiveReject(null); setReason(""); }} className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3]">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1">
      <span className="text-[#A3A3A3]">{k}</span>
      <span className="text-white text-right">{v || "—"}</span>
    </div>
  );
}
